import { Play, RotateCcw, CheckCircle2, Clock, Compass, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTour } from '@/contexts/TourContext';
import { getToursByPage } from '@/config/tours';
import type { Tour } from '@/types/product-tour';
import type { UserSegment } from '@/types/product-tour';
import { usePermissions } from '@/contexts/PermissionsContext';
import type { ProjectTabKey } from '@/types/roles.types';

interface Props {
  pageKey: string;
  onClose: () => void;
  userSegment?: UserSegment;
}

// Map HelpDrawer pageKeys to route paths
const PAGE_KEY_TO_PATH: Record<string, string> = {
  dashboard:                   '/dashboard',
  projects:                    '/projects',
  'project-details':           '/projects/',
  portfolio:                   '/portfolio',
  utilisation:                 '/utilisation',
  directory:                   '/directory',
  'roles-rates':               '/roles-rates',
  skills:                      '/skills',
  'wip-reporting':             '/reports/wip',
  wip:                         '/reports/wip',
  'project-status':            '/reports/project-status',
  'budget-variance':           '/reports/budget-variance',
  'project-pl':                '/reports/project-pl',
  'project-forecast':          '/reports/project-forecast',
  'profitability-report':      '/reports/profitability',
  profitability:               '/reports/profitability',
  'audit-log':                 '/audit-log',
  'project-transactions':      '/project-transactions',
  'project-templates':         '/project-templates',
  settings:                    '/settings',
  'settings-general':          '/settings/general',
  'settings-project':          '/settings/project',
  'settings-user-permissions': '/settings/user-permissions',
  'settings-integrations':     '/settings/integrations',
  'settings-notifications':    '/settings/notifications',
  'settings-user-preferences': '/settings/user-preferences',
  'settings-templates':        '/settings/templates',
};

function formatDuration(seconds?: number): string {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

interface TourCardProps {
  tour: Tour;
  onClose: () => void;
}

function TourCard({ tour, onClose }: TourCardProps) {
  const { getTourState, startTour, restartTour } = useTour();
  const state = getTourState(tour.id);
  const isCompleted = state?.status === 'completed';
  const isInProgress = state?.status === 'in_progress';

  const handleAction = () => {
    if (isCompleted) {
      restartTour(tour.id);
    } else {
      startTour(tour.id);
    }
    onClose();
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-border p-4 space-y-3 transition-colors',
        isCompleted ? 'bg-muted/30' : 'bg-background hover:bg-accent/20',
      )}
    >
      {/* Tour header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={cn(
              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              isCompleted
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400'
                : 'bg-primary/10 text-primary',
            )}
          >
            {isCompleted ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium leading-snug truncate">{tour.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">
              {tour.description}
            </p>
          </div>
        </div>

        {/* Status badge */}
        {isCompleted && (
          <Badge
            variant="secondary"
            className="shrink-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 border-0 text-[10px]"
          >
            Completed
          </Badge>
        )}
        {isInProgress && !isCompleted && (
          <Badge
            variant="secondary"
            className="shrink-0 bg-warning-100 text-warning-700 dark:bg-warning-950 dark:text-warning-400 border-0 text-[10px]"
          >
            In Progress
          </Badge>
        )}
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {tour.estimatedDuration && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(tour.estimatedDuration)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            {tour.steps.length} steps
          </span>
        </div>

        <Button
          size="sm"
          variant={isCompleted ? 'outline' : 'default'}
          className="h-7 text-xs gap-1.5 shrink-0"
          onClick={handleAction}
        >
          {isCompleted ? (
            <>
              <RotateCcw className="h-3 w-3" />
              Restart
            </>
          ) : isInProgress ? (
            <>
              <Play className="h-3 w-3" />
              Continue
            </>
          ) : (
            <>
              <Play className="h-3 w-3" />
              Start Tour
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function ToursTab({ pageKey, onClose, userSegment }: Props) {
  const path = PAGE_KEY_TO_PATH[pageKey] ?? `/${pageKey}`;
  const { isTabVisible } = usePermissions();

  // Filter tours by page and segment, then additionally hide any per-tab
  // project-details tour whose requiredTab the user cannot see.
  const pageTours = getToursByPage(path, userSegment).filter((tour) => {
    if (!tour.requiredTab) return true;
    return isTabVisible(tour.requiredTab as ProjectTabKey);
  });

  const { getTourState } = useTour();

  const completedCount = pageTours.filter(
    (t) => getTourState(t.id)?.status === 'completed',
  ).length;

  if (pageTours.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Compass className="h-6 w-6 text-muted-foreground/60" />
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">No tours available</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Product tours for this page will be available in a future update.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Interactive walkthroughs to guide you through key features on this page.
        </p>
        {pageTours.length > 0 && (
          <Badge variant="secondary" className="text-[10px] shrink-0">
            {completedCount}/{pageTours.length} completed
          </Badge>
        )}
      </div>

      {/* Tour list */}
      <div className="space-y-3">
        {pageTours.map((tour) => (
          <TourCard key={tour.id} tour={tour} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}
